import React, { useRef, useState, useEffect, useMemo, useCallback, Suspense } from "react";
import { Icon } from "@iconify/react";
import { Route, Switch, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@heroui/react";
import { ThemeSwitcher } from "./components/theme-switcher";
import { useTheme } from "@heroui/use-theme";
import { TruePortfolioProvider } from "./utils/TruePortfolioContext";
import { TruePortfolioSetupManager } from "./components/TruePortfolioSetupManager";
import { ProfileSettingsModal } from "./components/ProfileSettingsModal";
import { GlobalFilterProvider, useGlobalFilter } from "./context/GlobalFilterContext";
import { AccountingMethodProvider } from "./context/AccountingMethodContext";
import { TerminologyProvider } from "./context/TerminologyContext";
import { GlobalFilterBar } from "./components/GlobalFilterBar";
import { TradeTrackerLogo } from './components/icons/TradeTrackerLogo';
import { AnimatedBrandName } from './components/AnimatedBrandName';
import ErrorBoundary from "./components/ErrorBoundary";
import { Analytics } from '@vercel/analytics/react';
import { Loader } from "./components/Loader";
import ResetPasswordPage from './pages/reset-password';
import { AuthService } from './services/authService';

// Lazy load heavy components for better performance with preloading
const TradeJournal = React.lazy(() => import("./components/trade-journal").then(module => ({ default: module.TradeJournal })));
const TradeAnalytics = React.lazy(() => import("./components/trade-analytics").then(module => ({ default: module.TradeAnalytics })));
const TaxAnalytics = React.lazy(() => import("./components/tax-analytics").then(module => ({ default: module.TaxAnalytics })));
const MonthlyPerformanceTable = React.lazy(() => import("./pages/monthly-performance").then(module => ({ default: module.MonthlyPerformanceTable })));
const DeepAnalyticsPage = React.lazy(() => import("./pages/DeepAnalyticsPage"));

// Preload components for faster navigation
const preloadComponents = () => {
  // Preload most commonly accessed components
  import("./components/trade-analytics");
  import("./components/tax-analytics");
  import("./pages/monthly-performance");
};

// Authentication - Pure cloud-based
import { AuthProvider, useAuth, useUser } from "./context/AuthContext";
import { AuthGuard } from "./components/auth/AuthGuard";
import { SupabaseService } from "./services/supabaseService";
import { CachePreloader } from "./services/cachePreloader";
import { useCachePreloader } from "./hooks/use-cache-preloader";
import { AuthDebug } from "./components/debug/AuthDebug";
import { CacheMonitor } from "./components/debug/CacheStatus";
import { AuthModal } from "./components/auth/AuthModal";
import { AuthCallback } from "./pages/AuthCallback";
import { GapDownAnalysisWrapper } from "./components/GapDownAnalysisWrapper";
import { exportAllDataAsJSON } from "./utils/exportUtils";
import { addToast } from "@heroui/react";
// Pure cloud-based architecture with Supabase

// Main App Content Component (authenticated users only)
function AppContent() {
  const location = useLocation();
  const { theme } = useTheme();
  const { user, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isProfileOpen, setIsProfileOpen] = React.useState(false);
  const [userName, setUserName] = React.useState('');
  const [loadingPrefs, setLoadingPrefs] = React.useState(true);
  const [isFullWidthEnabled, setIsFullWidthEnabled] = React.useState(false);
  const [showAuthModal, setShowAuthModal] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [migrationStep, setMigrationStep] = React.useState<'initial' | 'download_instruction'>('initial');
  const [hasTrades, setHasTrades] = React.useState<boolean | null>(null);
  const [exportFormat, setExportFormat] = React.useState<'json' | 'gzip' | 'ultra'>('ultra');

  const mainContentRef = useRef<HTMLElement>(null);
  const [isMainContentFullscreen, setIsMainContentFullscreen] = useState(false);

  const getDefaultUserName = () => {
    // Use user's name from auth or fallback
    return user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  };

  // Helper function to get only the first name
  const getFirstName = (fullName: string) => {
    if (!fullName) return 'User';
    return fullName.split(' ')[0];
  };

  // Migration check removed - app is now purely cloud-based

  // Memoize Supabase helper functions to prevent re-creation on every render
  const fetchUserPreferences = useCallback(async () => {
    try {
      const prefs = await SupabaseService.getUserPreferences();
      return prefs;
    } catch (error) {
      return null;
    }
  }, []);

  const saveUserPreferences = useCallback(async (prefs: Partial<{ is_mobile_menu_open: boolean; is_profile_open: boolean; user_name: string; is_full_width_enabled: boolean }>) => {
    try {
      const existing = await fetchUserPreferences() || {};
      const updated = { ...existing, ...prefs };
      await SupabaseService.saveUserPreferences(updated);
    } catch (error) {
      // Debug logging removed for production
    }
  }, [fetchUserPreferences]);

  React.useEffect(() => {
    // Load preferences and check trades on mount
    const initializeUserData = async () => {
      try {
        // 1. Load User Preferences
        const prefs = await fetchUserPreferences();
        if (prefs) {
          setIsMobileMenuOpen(!!prefs.is_mobile_menu_open);
          setUserName(prefs.user_name || getDefaultUserName());
          setIsFullWidthEnabled(!!prefs.is_full_width_enabled);
        } else {
          setUserName(getDefaultUserName());
        }

        // 2. Check if user has trades (for migration logic)
        const _hasTrades = await SupabaseService.hasTrades();
        setHasTrades(_hasTrades);

      } catch (error) {
        setUserName(getDefaultUserName());
      } finally {
        setLoadingPrefs(false);
      }
    };

    if (user) {
      initializeUserData();
    }
  }, [fetchUserPreferences, user]);

  React.useEffect(() => {
    if (!loadingPrefs) {
      saveUserPreferences({ is_mobile_menu_open: isMobileMenuOpen });
    }
  }, [isMobileMenuOpen, loadingPrefs, saveUserPreferences]);

  // CRITICAL FIX: Don't save profile open state to prevent auto-opening on refresh
  // The profile modal should only be opened by user action, not persisted
  // React.useEffect(() => {
  //   if (!loadingPrefs) {
  //     saveUserPreferences({ is_profile_open: isProfileOpen });
  //   }
  // }, [isProfileOpen, loadingPrefs, saveUserPreferences]);

  React.useEffect(() => {
    if (!loadingPrefs) {
      saveUserPreferences({ user_name: userName });
    }
  }, [userName, loadingPrefs, saveUserPreferences]);

  React.useEffect(() => {
    if (!loadingPrefs) {
      // Use immediate save for critical layout preferences
      SupabaseService.saveUserPreferencesImmediate({ is_full_width_enabled: isFullWidthEnabled })
        .then((success) => {
          if (!success) {
            // Fallback to regular debounced save
            saveUserPreferences({ is_full_width_enabled: isFullWidthEnabled });
          }
        })
        .catch(() => {
          // Fallback to regular debounced save
          saveUserPreferences({ is_full_width_enabled: isFullWidthEnabled });
        });
    }
  }, [isFullWidthEnabled, loadingPrefs, saveUserPreferences]);

  const handleToggleMainContentFullscreen = () => {
    if (!document.fullscreenElement) {
      mainContentRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsMainContentFullscreen(document.fullscreenElement === mainContentRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // CRITICAL FIX: Initialize AuthService with JWT caching to prevent 2.8M+ auth calls
  React.useEffect(() => {
    AuthService.initialize().catch(error => {
      console.error('Failed to initialize AuthService:', error);
    });
  }, []);

  // PERFORMANCE OPTIMIZATION: Preload components and cache after initial render
  React.useEffect(() => {
    // Preload components after a short delay to not block initial render
    const timer = setTimeout(() => {
      preloadComponents();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // PERFORMANCE OPTIMIZATION: Intelligent cache preloading with user behavior tracking
  const cachePreloader = useCachePreloader({
    strategy: isFullWidthEnabled ? 'aggressive' : 'balanced',
    enableSmartPreloading: true,
    enableRouteBasedPreloading: true,
    preloadDelay: loadingPrefs ? 2000 : 1000 // Longer delay if still loading preferences
  });

  // Memoize navigation items to prevent unnecessary re-renders
  const navItems = useMemo(() => [
    { path: "/", name: "Journal", icon: "lucide:book-open" },
    { path: "/analytics", name: "Analytics", icon: "lucide:bar-chart-2" },
    { path: "/tax-analytics", name: "Tax Analytics", icon: "lucide:calculator" },
    { path: "/monthly-performance", name: "Monthly Performance", icon: "lucide:calendar-check" },
    { path: "/deep-analytics", name: "Deep Analytics", icon: "lucide:pie-chart" }
  ], []);



  return (
    <TruePortfolioProvider>
      <AccountingMethodProvider>
        <TerminologyProvider>
          <GlobalFilterProvider>
            <div className="min-h-screen bg-background font-sans antialiased">
              {/* Mandatory Migration Lockout Modal - Only for Authenticated Users */}
              {user && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-background/80 backdrop-blur-xl">
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full max-w-md relative overflow-hidden rounded-3xl border border-white/10 bg-black/5 dark:bg-white/5 shadow-2xl backdrop-blur-3xl saturate-150"
                  >
                    {/* Glass Reflection */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent pointer-events-none" />

                    {/* Sign Out Button - Top Left */}
                    <Button
                      isIconOnly
                      variant="light"
                      size="sm"
                      onPress={signOut}
                      className="absolute top-4 left-4 z-50 text-foreground/40 hover:text-red-500 hover:bg-red-500/10 transition-colors rounded-full"
                    >
                      <Icon icon="lucide:log-out" className="w-4 h-4" />
                    </Button>

                    {/* User Name - Top Right */}
                    <div className="absolute top-6 right-6 z-50 flex items-center gap-1.5 text-[10px] font-medium text-foreground/40 tracking-wide">
                      <Icon icon="lucide:user-circle" className="w-3.5 h-3.5" />
                      {userName}
                    </div>

                    <div className="relative px-8 py-10 flex flex-col items-center text-center gap-6">
                      {/* Icon */}
                      <TradeTrackerLogo className="w-8 h-8 text-foreground mb-1" />

                      {/* Content */}
                      <div className="flex-1 w-full space-y-10">
                        {migrationStep === 'initial' ? (
                          <>
                            <div className="space-y-3 mb-4">
                              <h2 className="text-2xl font-light text-foreground tracking-tight">
                                Thank You for Your Support
                              </h2>
                              <div className="flex items-center justify-center gap-2">
                                <span className="h-px w-4 bg-foreground/10" />
                                <p className="text-[9px] text-foreground/40 font-mono uppercase tracking-[0.3em]">
                                  Nexus 1.0 <span className="text-foreground/20 font-thin">→</span> Evolution
                                </p>
                                <span className="h-px w-4 bg-foreground/10" />
                              </div>
                            </div>

                            <div className="space-y-10">
                              <div className="space-y-4">
                                <p className="text-sm text-foreground/50 font-light leading-relaxed max-w-[320px] mx-auto">
                                  We've officially ended support for Nexus 1.0 to bring you something extraordinary. <span className="text-foreground/70">You won't regret it, we promise.</span>
                                </p>
                                <p className="text-[11px] text-foreground/30 font-light tracking-wide italic">
                                  Everything is ready for your transition:
                                </p>
                              </div>

                              <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 px-4 overflow-visible">
                                {[
                                  { icon: "lucide:layers", label: "Trades" },
                                  { icon: "lucide:image", label: "Charts" },
                                  { icon: "lucide:wallet", label: "Portfolio" },
                                  { icon: "lucide:calculator", label: "Tax" }
                                ].map((item, i) => (
                                  <div key={i} className="flex flex-col items-center gap-1.5 min-w-[60px] group transition-all">
                                    <Icon icon={item.icon} className="w-4 h-4 text-foreground/20 group-hover:text-foreground/40 transition-colors" />
                                    <span className="text-[8px] text-foreground/30 font-medium uppercase tracking-wider">{item.label}</span>
                                  </div>
                                ))}
                              </div>

                              <p className="text-[10px] text-foreground/20 font-light mono uppercase tracking-widest leading-loose">
                                {hasTrades === false
                                  ? "Account Verified • Move to Nexus 2.0"
                                  : "Encryption Ready • Package Secured"}
                              </p>

                              {hasTrades !== false && hasTrades !== null && (
                                <div className="pt-6 border-t border-foreground/5 space-y-4">
                                  <p className="text-[10px] text-foreground/40 font-medium tracking-wide uppercase">
                                    Choose the format that suits your requirement
                                  </p>
                                  <div className="flex flex-wrap justify-center gap-2">
                                    <button
                                      onClick={() => setExportFormat('ultra')}
                                      className={`group relative text-[9px] font-medium tracking-wider px-4 py-2 rounded-full transition-[background-color,border-color,color,ring] duration-150 ${exportFormat === 'ultra'
                                        ? 'bg-foreground/10 border-foreground/20 text-foreground ring-1 ring-foreground/10'
                                        : 'bg-transparent border-transparent text-foreground/30 hover:text-foreground/50'
                                        }`}
                                    >
                                      {exportFormat === 'ultra' && (
                                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-foreground text-[7px] text-background rounded-full font-bold uppercase tracking-[0.2em] shadow-lg border border-foreground/10 whitespace-nowrap">
                                          Recommended
                                        </span>
                                      )}
                                      JSON.GZ (Ultra)
                                    </button>
                                    <button
                                      onClick={() => setExportFormat('gzip')}
                                      className={`text-[9px] font-medium tracking-wider px-3 py-1.5 rounded-full transition-[background-color,border-color,color] duration-150 ${exportFormat === 'gzip'
                                        ? 'bg-foreground/10 border-foreground/20 text-foreground'
                                        : 'bg-transparent border-transparent text-foreground/30 hover:text-foreground/50'
                                        }`}
                                    >
                                      GZIP (Images)
                                    </button>
                                    <button
                                      onClick={() => setExportFormat('json')}
                                      className={`text-[9px] font-medium tracking-wider px-3 py-1.5 rounded-full transition-[background-color,border-color,color] duration-150 ${exportFormat === 'json'
                                        ? 'bg-foreground/10 border-foreground/20 text-foreground'
                                        : 'bg-transparent border-transparent text-foreground/30 hover:text-foreground/50'
                                        }`}
                                    >
                                      JSON
                                    </button>
                                  </div>

                                  {/* Dynamic Recommendation Text */}
                                  <div className="h-8 flex items-center justify-center">
                                    <motion.p
                                      initial={{ opacity: 0, y: 2 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      key={exportFormat}
                                      transition={{ duration: 0.15, ease: "easeOut" }}
                                      className="text-[10px] text-foreground/50 font-light italic text-center max-w-[280px]"
                                    >
                                      {exportFormat === 'ultra' && "✨ Smallest file size. Perfect for keeping all your chart images secure and fast to upload."}
                                      {exportFormat === 'gzip' && "Standard compression. Good for accounts with few charts."}
                                      {exportFormat === 'json' && "⚠️ Uncompressed raw data. May be very large (>50MB) if you have many screenshots."}
                                    </motion.p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="space-y-6">
                              <h2 className="text-2xl font-light text-foreground tracking-tight">
                                Backup Downloaded
                              </h2>
                              <p className="text-sm text-foreground/50 font-light leading-relaxed max-w-[280px] mx-auto">
                                Look for the <span className="font-medium text-foreground">Downloads icon</span> in your browser (top-right).
                                <br /><br />
                                Drag the file directly from there and drop it into the import box on the next screen.
                              </p>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Action */}
                      <Button
                        size="md"
                        variant="shadow"
                        className="group relative w-full h-10 bg-foreground text-background font-medium text-[10px] tracking-[0.2em] uppercase hover:opacity-90 transition-all rounded-full shadow-lg shadow-foreground/10"
                        isLoading={isLoading || hasTrades === null}
                        onPress={async () => {
                          if (hasTrades === null) return;

                          if (migrationStep === 'download_instruction' || hasTrades === false) {
                            const baseUrl = "https://www.nexusjournal.co.in";
                            const finalUrl = hasTrades === false ? baseUrl : `${baseUrl}/import`;
                            window.open(finalUrl, "_blank");
                            return;
                          }

                          setIsLoading(true);
                          addToast({
                            title: "Initiating Transfer",
                            description: "Starting secure backup (0%)...",
                            variant: "flat",
                            color: "default"
                          });

                          const isCompressed = exportFormat === 'gzip' || exportFormat === 'ultra';
                          const success = await exportAllDataAsJSON(isCompressed);

                          setIsLoading(false);

                          if (success) {
                            addToast({
                              title: "Backup Complete",
                              description: "File saved to downloads.",
                              variant: "flat",
                              color: "success"
                            });
                            setMigrationStep('download_instruction');
                          } else {
                            addToast({
                              title: "Export Failed",
                              description: "Please try again.",
                              variant: "flat",
                              color: "danger"
                            });
                          }
                        }}
                      >
                        <span className="relative z-10 flex items-center justify-center gap-2">
                          {hasTrades === null ? "Verifying Account..." : (
                            hasTrades === false ? "Take me to Nexus 2.0" : (isLoading ? "Packaging Data..." : (migrationStep === 'initial' ? "Execute Migration" : "Take me to Nexus 2.0"))
                          )}
                          {!isLoading && hasTrades !== null && <Icon icon="lucide:arrow-right" className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />}
                        </span>
                      </Button>

                      <div className="flex flex-col items-center gap-4">
                        <a
                          href="https://x.com/aniketm43686659"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-[9px] text-foreground/30 hover:text-foreground/50 transition-all px-3 py-1.5 rounded-full border border-foreground/5 hover:bg-foreground/5 tracking-wider"
                        >
                          <Icon icon="ri:twitter-x-fill" className="w-3 h-3" />
                          <span>ISSUES WITH TRANSITION? DM US ON X</span>
                        </a>

                        <p className="text-[10px] text-foreground/20 font-mono uppercase tracking-[0.3em]">
                          Secure • Encrypted • Automatic
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              )}



              {/* Navigation */}
              <header className="sticky top-0 z-40 w-full border-b border-gray-200 dark:border-gray-700 bg-background/80 backdrop-blur-xl backdrop-saturate-150">
                <nav className="px-4 sm:px-6">
                  <div className="flex h-16 items-center justify-between">
                    {/* Logo and Mobile Menu Button */}
                    <div className="flex items-center gap-4">
                      <Link
                        to="/"
                        className="flex items-center gap-2 font-semibold tracking-tight text-foreground hover:opacity-90 transition-opacity"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className="h-5 w-5 text-foreground"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          {/* Outer circle */}
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                          />
                          {/* Diamond/gem shape */}
                          <path
                            d="M12 6L16 10L12 18L8 10L12 6Z"
                            fill="currentColor"
                            stroke="currentColor"
                            strokeWidth="0.5"
                            strokeLinejoin="round"
                          />
                          {/* Inner diamond lines */}
                          <path
                            d="M8 10L12 14L16 10"
                            stroke="currentColor"
                            strokeWidth="0.5"
                            fill="none"
                            opacity="0.7"
                          />
                        </svg>
                        <AnimatedBrandName className="text-foreground" />
                      </Link>
                      <Button
                        isIconOnly
                        variant="light"
                        size="sm"
                        onPress={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="sm:hidden"
                      >
                        <Icon icon={isMobileMenuOpen ? "lucide:x" : "lucide:menu"} className="h-5 w-5" />
                      </Button>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden sm:flex sm:items-center sm:gap-8">
                      {navItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onMouseEnter={() => {
                              // PERFORMANCE: Predictive preloading on hover
                              cachePreloader.predictivePreload(item.path);
                            }}
                            className={`flex items-center gap-2.5 px-3 py-2 text-sm font-medium transition-colors rounded-lg
                          ${isActive
                                ? 'text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 backdrop-blur-md shadow-md'
                                : 'text-gray-700 dark:text-gray-300 hover:text-foreground hover:bg-gray-100 dark:hover:bg-gray-800/50 backdrop-blur-sm transition-all duration-300'
                              }`}
                          >
                            <Icon icon={item.icon} className="h-4 w-4" />
                            {item.name}
                          </Link>
                        );
                      })}
                    </div>

                    {/* Right Side Actions */}
                    <div className="flex items-center gap-3">
                      <ThemeSwitcher />
                      {user ? (
                        <>
                          <Button
                            variant="flat"
                            size="sm"
                            onPress={() => setIsProfileOpen(true)}
                            className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/20 bg-white/10 backdrop-blur-md hover:bg-white/20 transition-all duration-300 min-h-0 min-w-0 shadow-sm"
                            startContent={<Icon icon="lucide:user" className="h-4 w-4" />}
                          >
                            <span className="font-medium text-sm leading-none">{getFirstName(userName)}</span>
                          </Button>
                          <Button
                            isIconOnly
                            variant="light"
                            size="sm"
                            onPress={signOut}
                            className="hidden sm:flex hover:bg-red-100 dark:hover:bg-red-900/20 transition-all duration-300"
                          >
                            <Icon icon="lucide:log-out" className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="flat"
                          size="sm"
                          onPress={() => setShowAuthModal(true)}
                          className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/20 bg-white/10 backdrop-blur-md hover:bg-white/20 transition-all duration-300 min-h-0 min-w-0 shadow-sm"
                          startContent={<Icon icon="lucide:log-in" className="h-4 w-4" />}
                        >
                          <span className="font-medium text-sm leading-none">Sign In</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </nav>

                {/* Mobile Navigation */}
                <AnimatePresence>
                  {isMobileMenuOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="sm:hidden border-t border-divider overflow-hidden"
                    >
                      <div className="space-y-1 px-4 py-3 bg-background/30 backdrop-blur-xl">
                        {navItems.map((item) => {
                          const isActive = location.pathname === item.path;
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              onTouchStart={() => {
                                // PERFORMANCE: Predictive preloading on touch (mobile)
                                cachePreloader.predictivePreload(item.path);
                              }}
                              className={`flex items-center gap-2.5 px-3 py-2 text-sm font-medium transition-colors rounded-lg
                          ${isActive
                                  ? 'text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 backdrop-blur-md shadow-md'
                                  : 'text-gray-700 dark:text-gray-300 hover:text-foreground hover:bg-gray-100 dark:hover:bg-gray-800/50 backdrop-blur-sm transition-all duration-300'
                                }`}
                            >
                              <Icon icon={item.icon} className="h-4 w-4" />
                              {item.name}
                            </Link>
                          );
                        })}
                        {user ? (
                          <>
                            {/* Profile Button for Mobile */}
                            <Button
                              variant="light"
                              size="sm"
                              onPress={() => {
                                setIsProfileOpen(true);
                                setIsMobileMenuOpen(false); // Close mobile menu when opening profile
                              }}
                              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium transition-colors rounded-lg text-gray-700 dark:text-gray-300 hover:text-foreground hover:bg-gray-100 dark:hover:bg-gray-800/50 backdrop-blur-sm transition-all duration-300"
                              startContent={<Icon icon="lucide:user" className="h-4 w-4" />}
                            >
                              <span>{getFirstName(userName) || 'Profile'}</span>
                            </Button>
                            {/* Sign Out Button for Mobile */}
                            <Button
                              isIconOnly
                              variant="light"
                              size="sm"
                              onPress={() => {
                                signOut();
                                setIsMobileMenuOpen(false);
                              }}
                              className="flex items-center justify-center text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20 transition-all duration-300"
                            >
                              <Icon icon="lucide:log-out" className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="light"
                            size="sm"
                            onPress={() => {
                              setShowAuthModal(true);
                              setIsMobileMenuOpen(false);
                            }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium transition-colors rounded-lg text-gray-700 dark:text-gray-300 hover:text-foreground hover:bg-gray-100 dark:hover:bg-gray-800/50 backdrop-blur-sm transition-all duration-300"
                            startContent={<Icon icon="lucide:log-in" className="h-4 w-4" />}
                          >
                            <span>Sign In</span>
                          </Button>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </header>

              {/* Global Filter Bar */}
              <GlobalFilterBar />

              {/* Main Content */}
              <main ref={mainContentRef} className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
                <ErrorBoundary>
                  <div className={isFullWidthEnabled ? "py-6" : "max-w-7xl mx-auto py-6"}>
                    <Suspense fallback={<Loader />}>
                      <Switch>
                        <Route path="/auth/callback">
                          <AuthCallback />
                        </Route>
                        <Route path="/analytics">
                          <TradeAnalytics />
                        </Route>
                        <Route exact path="/" render={(props) => (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <TradeJournal {...props} toggleFullscreen={handleToggleMainContentFullscreen} isFullscreen={isMainContentFullscreen} />
                          </motion.div>
                        )} />
                        <Route path="/tax-analytics" component={TaxAnalytics} />
                        <Route path="/monthly-performance" component={MonthlyPerformanceTable} />
                        <Route path="/deep-analytics" component={DeepAnalyticsPage} />
                        <Route path="/reset-password" component={ResetPasswordPage} />
                      </Switch>
                    </Suspense>
                  </div>
                </ErrorBoundary>

                {/* Gap Down Analysis - positioned inside providers */}
                <GapDownAnalysisWrapper />
              </main>

              <ProfileSettingsModal
                isOpen={isProfileOpen}
                onOpenChange={setIsProfileOpen}
                userName={userName}
                setUserName={setUserName}
                isFullWidthEnabled={isFullWidthEnabled}
                setIsFullWidthEnabled={setIsFullWidthEnabled}
              />

              {/* Only show TruePortfolio setup for authenticated users */}
              {user && (
                <TruePortfolioSetupManager
                  userName={userName}
                  setUserName={setUserName}
                />
              )}

              {/* Migration removed - app is now purely cloud-based */}

              <Analytics />
              {/* <AuthDebug /> */}
              <CacheMonitor />

              {/* Auth Modal for Guest Users */}
              {showAuthModal && (
                <AuthModal
                  isOpen={showAuthModal}
                  onClose={() => setShowAuthModal(false)}
                />
              )}
            </div>
          </GlobalFilterProvider>
        </TerminologyProvider>
      </AccountingMethodProvider >
    </TruePortfolioProvider >
  );
}

// Maintenance Mode Component
function MaintenanceMode() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="space-y-4">
          <div className="mx-auto w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
            <Icon icon="lucide:wrench" className="w-8 h-8 text-orange-600 dark:text-orange-400" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            Under Maintenance
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            We're currently performing maintenance to improve your experience.
            We'll be back soon!
          </p>
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-500">

        </div>
      </div>
    </div>
  );
}

// Main App Component with Authentication
export default function App() {
  // Set to true to enable maintenance mode
  const isMaintenanceMode = false;

  if (isMaintenanceMode) {
    return <MaintenanceMode />;
  }

  return (
    <AuthProvider>
      <AuthGuard>
        <AppContent />
      </AuthGuard>
    </AuthProvider>
  );
}
